import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import { Arrow } from '../components/home/Hero';
import {
  UI, EASE, wrap, wrapN, pheroIn, hXl, hS, eyebrow, sec, card, chip,
  buybox, sidebarL, sidebarMain, sidebarSide, iconbtn, textbtn, textbtnDanger,
  sumRow, btn, btnYellow, price as priceSx, mrp, empty, num,
} from '../styles/tl5';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const TrashIcon = (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: 18, height: 18, minWidth: 18, display: 'block' }}
  >
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    <path d="M10 11v6M14 11v6" />
  </Box>
);

// Packages get the shield, single tests the flask — the same two glyphs the
// catalogue cards use, so an item is recognisable from its row alone.
const ItemIcon = ({ pkg }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: 20, height: 20, minWidth: 20, display: 'block' }}
  >
    {pkg
      ? <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></>
      : <><path d="M9 3h6M10 3v6L5 19a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 19l-5-10V3" /><path d="M7.5 15h9" /></>}
  </Box>
);

const Cart = () => {
  const navigate = useNavigate();
  const { items, removeItem, clearCart, totals } = useCart();
  const { showToast } = useUI();

  // Home collection is free, so the total is the subtotal. Kept as a named
  // value rather than inlined, since this is where a fee would land.
  const HOME_FEE = 0;
  const grand = totals.subtotal + HOME_FEE;

  if (items.length === 0) {
    return (
      <Box component="section" sx={sec}>
        <Box sx={{ ...wrapN, ...empty }}>
          <Typography sx={{ fontSize: 34, lineHeight: 1 }}>🛒</Typography>
          <Typography component="h1" sx={hS}>Your cart is empty</Typography>
          <Typography sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)', maxWidth: 380 }}>
            Browse our tests and packages to add something to your cart.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', mt: '6px' }}>
            <Box component="button" type="button" onClick={() => navigate(ROUTES.TESTS)} sx={btn}>
              Browse tests
              <Arrow size={16} />
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => navigate(ROUTES.PACKAGES)}
              sx={{
                ...btn,
                background: 'transparent',
                color: 'var(--tl-g9)',
                boxShadow: 'inset 0 0 0 1.5px var(--tl-line-2)',
                '&:hover': { background: 'var(--tl-mint)', color: 'var(--tl-g9)', transform: 'translateY(-2px)' },
              }}
            >
              View packages
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* ═══ HEADING — sits straight on the page, no hero band ═══ */}
      <Box component="section">
        <Box sx={{ ...wrap, ...pheroIn, gap: '12px' }}>
          <Box component="span" sx={eyebrow}>Your cart</Box>
          <Typography component="h1" sx={hXl}>Review your order</Typography>
        </Box>
      </Box>

      {/* ═══ ITEMS + SUMMARY ═══ */}
      <Box component="section" sx={{ pb: 'clamp(32px, 3.6vw, 56px)' }}>
        <Box sx={{ ...wrap, ...sidebarL }}>
          <Box sx={sidebarMain}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', mb: '14px' }}>
              <Typography sx={{ ...num, fontFamily: UI, fontSize: 14.5, color: 'var(--tl-muted)' }}>
                {totals.count} item{totals.count === 1 ? '' : 's'}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => { clearCart(); showToast('Cart cleared'); }}
                sx={textbtnDanger}
              >
                Clear cart
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {items.map((it, i) => {
                const isPkg = it.type === 'pkg';
                return (
                  <Box
                    key={`${it.type}-${it.id}`}
                    sx={{
                      ...card,
                      // On a phone the price and the bin squeeze the title
                      // into three lines, so the price drops under it and
                      // only the bin stays on the right.
                      display: 'grid',
                      gridTemplateColumns: { xs: '48px 1fr 44px', sm: '48px 1fr auto 44px' },
                      gridTemplateAreas: {
                        xs: '"ico text del" "ico price del"',
                        sm: '"ico text price del"',
                      },
                      alignItems: 'center',
                      columnGap: '16px',
                      rowGap: '8px',
                      p: '18px 18px 18px 20px',
                      animation: `tl-stgin .45s ${EASE} both`,
                      animationDelay: `${Math.min(i, 6) * 45}ms`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        placeItems: 'center',
                        gridArea: 'ico',
                        alignSelf: { xs: 'start', sm: 'center' },
                        width: 48,
                        height: 48,
                        flex: 'none',
                        borderRadius: '14px',
                        background: 'var(--tl-teal-50)',
                        color: '#0E6455',
                      }}
                    >
                      <ItemIcon pkg={isPkg} />
                    </Box>

                    <Box sx={{ gridArea: 'text', minWidth: 0 }}>
                      <Box component="span" sx={{ ...chip, height: 26, fontSize: 12, mb: '6px' }}>
                        {isPkg ? 'Package' : 'Test'}
                      </Box>
                      <Typography
                        sx={{ fontFamily: UI, fontSize: 16, fontWeight: 700, lineHeight: 1.3, color: 'var(--tl-g9)' }}
                      >
                        {it.name}
                      </Typography>
                      {(it.paramCount > 0 || it.tat) && (
                        <Typography sx={{ fontFamily: UI, fontSize: 13.5, color: 'var(--tl-muted)', mt: '2px' }}>
                          {it.paramCount > 0 && `${it.paramCount} parameter${it.paramCount === 1 ? '' : 's'}`}
                          {it.paramCount > 0 && it.tat && ' · '}
                          {it.tat && `Report in ${it.tat}`}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ gridArea: 'price', textAlign: { xs: 'left', sm: 'right' } }}>
                      <Box component="div" sx={{ ...priceSx, fontSize: 20 }}>{fmtINR(it.price)}</Box>
                      {it.original > it.price && (
                        <Box component="div" sx={{ ...mrp, fontSize: 13, mt: '2px' }}>{fmtINR(it.original)}</Box>
                      )}
                    </Box>

                    <Box
                      component="button"
                      type="button"
                      aria-label={`Remove ${it.name}`}
                      onClick={() => { removeItem(it.id, it.type); showToast('Removed from cart'); }}
                      sx={{ ...iconbtn, gridArea: 'del' }}
                    >
                      {TrashIcon}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box
              component="button"
              type="button"
              onClick={() => navigate(ROUTES.TESTS)}
              sx={{ ...textbtn, display: 'inline-flex', alignItems: 'center', gap: '10px', mt: '18px', color: 'var(--tl-g9)' }}
            >
              <Arrow size={15} dir="left" />
              Continue shopping
            </Box>
          </Box>

          {/* The aside must span the row for the sticky card to have
              anywhere to travel — see the note on `sidebarL`. */}
          <Box component="aside" sx={{ ...sidebarSide, alignSelf: { md: 'stretch' } }}>
            <Box sx={{ ...buybox, position: { md: 'sticky' }, top: { md: 96 } }}>
              <Typography component="h2" sx={hS}>Order summary</Typography>

              <Box sx={{ mt: '10px' }}>
                <Box sx={sumRow}>
                  <Box component="span" sx={{ color: 'var(--tl-muted)' }}>Subtotal</Box>
                  <Box component="span" sx={{ ...num, fontWeight: 700 }}>{fmtINR(totals.subtotal)}</Box>
                </Box>
                {totals.savings > 0 && (
                  <Box sx={sumRow}>
                    <Box component="span" sx={{ color: 'var(--tl-muted)' }}>You save</Box>
                    <Box component="span" sx={{ ...num, fontWeight: 700, color: 'var(--tl-teal-d)' }}>
                      −{fmtINR(totals.savings)}
                    </Box>
                  </Box>
                )}
                <Box sx={sumRow}>
                  <Box component="span" sx={{ color: 'var(--tl-muted)' }}>Home collection</Box>
                  <Box component="span" sx={{ fontWeight: 700, color: 'var(--tl-teal-d)' }}>Free</Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px', py: '18px' }}>
                <Typography sx={{ fontFamily: UI, fontSize: 17, fontWeight: 800, color: 'var(--tl-g9)' }}>Total</Typography>
                <Box component="span" sx={{ ...priceSx, fontSize: 30 }}>{fmtINR(grand)}</Box>
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => navigate(ROUTES.BOOKING)}
                sx={{ ...btnYellow, width: '100%' }}
              >
                Proceed to Booking
                <Arrow size={16} />
              </Box>

              <Typography
                sx={{ fontFamily: UI, fontSize: 13, lineHeight: 1.5, color: 'var(--tl-muted)', textAlign: 'center', mt: '14px' }}
              >
                NABL verified · Doctor-signed reports · 60 min Trustian
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Cart;
